import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, FlexControlledForm, Preloader, Text, useResponsive } from '@common-fe/common-fe';

import AppButton from '@/components/controls/AppButton';
import { WarnModal } from '@/components/elements';
import { useAuthStore } from '@/modules/user';
import useEmployeeViewDataQuery from '@/modules/user/queries/useEmployeeViewData.query';

import useCardReplaceModalStore from '../../store/useCardReplaceModal.store';

import useCardAddressModalFields from './hooks/useCardAddressModalFields';
import useCardReplacementQuery, { ReplacementInitiationType } from './hooks/useCardReplacement.query';
import useCardReplacementReasonFields from './hooks/useCardReplacementReasonFields';

const EMPTY_VALUE = '-';

interface Props {
  replacementInitiationType?: ReplacementInitiationType;
  onCancel?: (value: boolean) => void;
  setDoneMode?: () => void;
}

const CardReplaceFormModal: React.FC<Props> = ({ replacementInitiationType, onCancel, setDoneMode }) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const [errorModal, setErrorModal] = useState(false);
  const [cardSetupReason, setCardSetupReason] = useState(false);
  const [isSubmit, setSubmit] = useState(false);
  const { onSave, isLoading } = useCardReplacementQuery();
  const { user } = useAuthStore();
  const { formattedEmployee } = useEmployeeViewDataQuery(user?.employee?.id);

  const currentAddressParams = useMemo(
    () => ({
      STREET_ADDRESS_1: formattedEmployee?.addresses?.[0]?.line1 || EMPTY_VALUE,
      STREET_ADDRESS_2: formattedEmployee?.addresses?.[0]?.line2 || EMPTY_VALUE,
      CITY: formattedEmployee?.addresses?.[0]?.city || EMPTY_VALUE,
      STATE: formattedEmployee?.addresses?.[0]?.state || EMPTY_VALUE,
      ZIP_CODE: formattedEmployee?.addresses?.[0]?.zipcode || EMPTY_VALUE,
    }),
    [formattedEmployee]
  );

  const addressFields = useCardAddressModalFields({
    defaultFields: currentAddressParams,
  });

  const {
    state: { cardID },
    handleReset,
  } = useCardReplaceModalStore();
  const replacementFields = useCardReplacementReasonFields();

  const showError = useMemo(() => isSubmit && !cardSetupReason, [cardSetupReason, isSubmit]);

  const [formValues, setFormValues] = useState({
    reason: '',
  });

  const handleChangeReasonValues = useCallback((values: { reason: string }) => {
    const newValues = { ...values };
    const filledObj = Object.values(newValues).every(Boolean);
    setFormValues((prev) => ({ ...prev, ...values }));
    if (filledObj) {
      setCardSetupReason(true);
    } else setCardSetupReason(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    setSubmit(true);
    if (cardSetupReason) {
      try {
        await onSave({
          cardID,
          data: formValues,
          replacementInitiationType,
        });
        if (!isLoading && setDoneMode) {
          setDoneMode();
        }
      } catch (e) {
        setErrorModal(true);
      }
    }
  }, [cardSetupReason, onSave, cardID, formValues, replacementInitiationType, isLoading, setDoneMode]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel(false);
      handleReset();
    }
  }, [handleReset, onCancel]);

  return (
    <>
      <Box>
        <Box direction="column">
          <Box round="container1Round" pad={isMobile ? 'spacing8' : 'spacing24'} background="module">
            <Box data-testid="ReplacementReasonForm" round="container1Round" background="canvas">
              <FlexControlledForm
                // formTitle="Why you need a new card?"
                fields={replacementFields}
                editMode
                onChangeValues={handleChangeReasonValues}
                showError={showError}
                isModalType
              />
            </Box>
            {cardSetupReason && (
              <Box
                data-testid="ReplacementAddressForm"
                round="container1Round"
                background="canvas"
                margin={{ top: isMobile ? 'spacing12' : 'spacing24' }}
              >
                <FlexControlledForm
                  formTitle="Your Card Will Be Sent To"
                  subTitle={
                    <Box margin={{ top: 'spacing4', bottom: '20px' }}>
                      <Text size="medium" weight={500} color="textBody">
                        If this address is incorrect, please update with your employer.
                      </Text>
                    </Box>
                  }
                  fields={addressFields}
                  editMode
                  onChangeValues={() => null}
                  isModalType
                />
              </Box>
            )}
          </Box>
        </Box>
        <Box
          direction={isMobile ? 'column-reverse' : 'row'}
          fill="horizontal"
          margin={{ top: showError ? 'spacing12' : isMobile ? 'spacing16' : 'spacing24' }}
          align="end"
          justify="end"
          width="medium"
          gap={isMobile ? 'spacing12' : 'spacing8'}
          pad={isMobile ? { horizontal: 'spacing4' } : undefined}
        >
          <AppButton
            buttonType="secondary"
            width={isMobile ? 'full' : 'control'}
            type="button"
            onClick={handleCancel}
          >
            Cancel
          </AppButton>
          <AppButton
            disabled={isLoading}
            type="button"
            onClick={handleSubmit}
            width={isMobile ? 'full' : 'control'}
          >
            {isLoading ? <Preloader color="white" /> : 'Submit'}
          </AppButton>
        </Box>
      </Box>
      <WarnModal
        testId="replace-error-modal"
        visible={errorModal}
        onSetVisible={setErrorModal}
        header={t('The server encountered an error processing the request')}
        helptext={t(`It doesn't appear to have affected your data, but we cannot process your request.
        Our technical staff have been automatically notified and will be looking into this with the utmost urgency.`)}
        buttonText={t('Close')}
        onSubmit={handleSubmit}
        submitButtonText={t('Try again')}
      />
    </>
  );
};

export default CardReplaceFormModal;
